import { useState, useRef, useCallback } from 'react';
import { Search, X } from 'lucide-react';
import { useMapStore } from '@/stores/mapStore';
import type { PointGeometry } from '@/types/map';
import { getVehicleColorMeta, getVehicleDescriptor } from '@/utils/vehicleIdentity';

export default function VehicleSearch() {
  const vehicleFeatures = useMapStore((s) => s.vehicleFeatures);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{
    alias: string;
    customer: string;
    plate: string;
    economic: string;
    descriptor: string;
    color: string;
    lat: number;
    lon: number;
  }[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(
    (q: string) => {
      setQuery(q);
      if (!q.trim()) {
        setResults([]);
        return;
      }
      const term = q.toLowerCase();
      const matches = vehicleFeatures
        .filter((f) => {
          const p = f.properties;
          const haystack = [
            p.alias,
            p.label,
            p.customer,
            p.destination_name,
            p.plate,
            p.economic,
            p.vehicle_type,
            p.brand,
            p.model,
            p.vehicle_color,
          ]
            .filter(Boolean)
            .join(' ')
            .toLowerCase();
          return haystack.includes(term);
        })
        .slice(0, 15)
        .map((f) => {
          const geom = f.geometry as PointGeometry;
          const descriptor = getVehicleDescriptor(f.properties) || '';
          const color = getVehicleColorMeta(f.properties)?.label || '';
          return {
            alias: f.properties.alias || f.properties.label || '',
            customer: f.properties.customer || '',
            plate: String(f.properties.plate || ''),
            economic: String(f.properties.economic || ''),
            descriptor,
            color,
            lat: geom.coordinates[1],
            lon: geom.coordinates[0],
          };
        });
      setResults(matches);
    },
    [vehicleFeatures]
  );

  const selectResult = (r: { alias: string; lat: number; lon: number }) => {
    window.dispatchEvent(new CustomEvent('open-vehicle-popup', { detail: { alias: r.alias, lat: r.lat, lon: r.lon } }));
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleBlur = () => {
    timeoutRef.current = setTimeout(() => {
      if (!query.trim()) setOpen(false);
      setResults([]);
    }, 200);
  };

  const handleFocus = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
    if (query.trim()) search(query);
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="bg-bg-secondary/90 backdrop-blur-xl border border-border-strong rounded-xl px-3 py-2 shadow-lg flex items-center gap-2 text-text-muted hover:border-accent-blue transition-all cursor-pointer"
      >
        <Search size={12} />
        <span className="text-[11px]">Buscar unidad...</span>
      </button>
    );
  }

  return (
    <div className="relative w-[220px]">
      <div className="bg-bg-secondary/90 backdrop-blur-xl border border-accent-blue rounded-xl px-3 py-1.5 shadow-lg flex items-center gap-2">
        <Search size={12} className="text-text-muted flex-shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => search(e.target.value)}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder="Alias, placa, eco, marca..."
          className="bg-transparent border-none outline-none text-xs text-text-primary placeholder:text-text-subtle w-full"
          autoFocus
        />
        <button
          onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
          className="text-text-muted hover:text-text-primary flex-shrink-0"
        >
          <X size={12} />
        </button>
      </div>

      {results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-bg-secondary/95 backdrop-blur-xl border border-border-strong rounded-lg shadow-xl overflow-hidden z-10 max-h-[240px] overflow-y-auto">
          {results.map((r, i) => (
            <button
              key={i}
              onMouseDown={() => selectResult(r)}
              className="w-full text-left px-3 py-1.5 hover:bg-bg-tertiary transition-colors border-b border-border-subtle/30 last:border-b-0"
            >
              <div className="text-xs font-semibold text-text-primary truncate">{r.alias}</div>
              <div className="text-[10px] text-text-muted truncate">
                {[
                  r.customer,
                  r.descriptor,
                  r.color ? `Color ${r.color}` : '',
                  r.plate ? `Placa ${r.plate}` : '',
                  r.economic ? `Eco ${r.economic}` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}

      {query.trim() && results.length === 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 bg-bg-secondary/95 backdrop-blur-xl border border-border-strong rounded-lg shadow-xl p-2">
          <span className="text-[10px] text-text-subtle">Sin resultados</span>
        </div>
      )}
    </div>
  );
}
